import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  FormFeedback,
  Spinner,
  Alert,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import progileBg from "../../assets/images/profile-bg.jpg";
import { setAuthorization } from "../../helpers/api.helper";
import avatar1 from "../../assets/images/users/avatar-11.png";
import { useProfile } from "../../Components/Hooks/UserHooks";
import {
  changePassword,
  editProfile,
  getProfile,
  resetProfileFlag,
  updateProfileAvatar,
  getSettings,
  updateSettings,
} from "../../store/actions";

const SettingsPage = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("1");
  const tabChange = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };
  const { userProfile, token } = useProfile();
  const {
    user,
    loading,
    error,
    success,
    chPassError,
    chPassSuccess,
    chPassLoading,
    updateAvatarError,
  } = useSelector((state) => state.Profile);

  const { settings, updateSettingsLoading } = useSelector(
    (state) => state.Settings
  );

  document.title = `${t("Settings")}  | ${t(process.env.REACT_APP_HEADING)}`;

  useEffect(() => {
    if (userProfile && token) {
      setAuthorization(token);
      dispatch(getProfile());
      dispatch(getSettings());
    }
  }, []);

  /**
   * Formik handling
   */
  const profileForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      mobile: user.mobile || "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .max(50, t("Validations.Common.Max", { length: 50 }))
        .required(t("Validations.FirstName.Required")),
      lastName: Yup.string()
        .max(50, t("Validations.Common.Max", { length: 50 }))
        .required(t("Validations.LastName.Required")),
      mobile: Yup.string().max(12, t("Validations.Common.Max", { length: 12 })),
    }),

    onSubmit: (values) => {
      dispatch(editProfile(values, props.history));
    },
  });

  const getAvatar = () => {
    if (user && user.avatar) {
      return user.avatar;
    } else {
      return avatar1;
    }
  };

  const changePasswordForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      oldPassword: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string()
        .min(8, t("Validations.Common.Min", { length: 8 }))
        .max(25, t("Validations.Common.Max", { length: 25 }))
        .required(t("Validations.CurrentPassword.Required")),
      password: Yup.string()
        .min(8, t("Validations.Common.Min", { length: 8 }))
        .max(25, t("Validations.Common.Max", { length: 25 }))
        .required(t("Validations.NewPassword.Required")),
      confirmPassword: Yup.string()
        .min(8, t("Validations.Common.Min", { length: 8 }))
        .max(25, t("Validations.Common.Max", { length: 25 }))
        .oneOf([Yup.ref("password"), null], t("Validations.Password.Match"))
        .required(t("Validations.ConfirmPassword.Required")),
    }),

    onSubmit: (values) => {
      dispatch(changePassword(values, props.history));
    },
  });

  const otherSettingsForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      isPaymentEnabled: settings.isPaymentEnabled || false,
      isPhoneVerificationRequiredForSignup:
        settings.isPhoneVerificationRequiredForSignup || false,
    },
    validationSchema: Yup.object({
      isPaymentEnabled: Yup.bool(),
      isPhoneVerificationRequiredForSignup: Yup.bool(),
    }),

    onSubmit: (values) => {
      dispatch(updateSettings(values, props.history));
    },
  });

  useEffect(() => {
    setTimeout(() => {
      dispatch(resetProfileFlag());
    }, 3000);
  }, [dispatch, error, chPassError]);

  // handle change event of input file and validate it
  const onChangeAvatar = (event) => {
    const imageFile = event.target.files[0];

    if (!imageFile) {
      this.setState({
        invalidImage: t("Validations.Image.Please select image"),
      });
      return false;
    }

    if (!imageFile.name.match(/\.(jpg|jpeg|png)$/)) {
      this.setState({
        invalidImage: t("Validations.Image.Please select valid image"),
      });
      return false;
    }
    dispatch(updateProfileAvatar(imageFile));
  };

  useEffect(() => {
    setTimeout(() => {
      dispatch(resetProfileFlag());
    }, 3000);
  }, [dispatch, error]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="position-relative mx-n4 mt-n4">
            <div className="profile-wid-bg profile-setting-img">
              <img src={progileBg} className="profile-wid-img" alt="" />
              <div className="overlay-content">
                <div className="text-end p-3"></div>
              </div>
            </div>
          </div>
          <Row>
            <Col xxl={3}>
              <Card className="mt-n5">
                <CardBody className="p-4">
                  <div className="text-center">
                    <div className="profile-user position-relative d-inline-block mx-auto  mb-4">
                      {updateAvatarError && updateAvatarError ? (
                        <Alert color="danger">
                          {" "}
                          {t(`Messages.Error.${updateAvatarError}`)}{" "}
                        </Alert>
                      ) : null}
                      <img
                        src={getAvatar()}
                        type="file"
                        className="rounded-circle avatar-xl img-thumbnail user-profile-image"
                        alt="user-profile"
                      />
                      <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                        <Input
                          id="profile-img-file-input"
                          type="file"
                          className="profile-img-file-input"
                          accept="image/*"
                          onChange={onChangeAvatar}
                        />
                        <Label
                          htmlFor="profile-img-file-input"
                          className="profile-photo-edit avatar-xs"
                        >
                          <span className="avatar-title rounded-circle bg-light text-body">
                            <i className="ri-camera-fill"></i>
                          </span>
                        </Label>
                      </div>
                    </div>
                    <h5 className="fs-16 mb-1">
                      {user?.firstName} {user?.lastName}
                    </h5>
                    <p className="text-muted mb-0">{user?.role}</p>
                  </div>
                  <p className="small text-center text-muted">
                    {t(
                      "Messages.Information.Supported file types are jpg and png"
                    )}
                  </p>
                </CardBody>
              </Card>
            </Col>

            <Col xxl={9}>
              <Card className="mt-xxl-n5">
                <CardHeader>
                  <Nav
                    className="nav-tabs-custom rounded card-header-tabs border-bottom-0"
                    role="tablist"
                  >
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "1" })}
                        onClick={() => {
                          tabChange("1");
                        }}
                      >
                        <i className="fas fa-home"></i>
                        {t("Common.Personal Details")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        to="#"
                        className={classnames({ active: activeTab === "2" })}
                        onClick={() => {
                          tabChange("2");
                        }}
                        type="button"
                      >
                        <i className="far fa-user"></i>
                        {t("Common.Change Password")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "3" })}
                        onClick={() => {
                          tabChange("3");
                        }}
                      >
                        <i className="fas fa-home"></i>
                        {t("Common.Other Settings")}
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardHeader>
                <CardBody className="p-4">
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      {error && error ? (
                        <Alert color="danger" style={{ marginTop: "13px" }}>
                          {t(`Messages.Error.${error}`)}{" "}
                        </Alert>
                      ) : null}
                      {success ? (
                        <Alert color="success" style={{ marginTop: "13px" }}>
                          {t(`Messages.Success.${success}`)}
                        </Alert>
                      ) : null}
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          profileForm.handleSubmit();
                          return false;
                        }}
                        action="#"
                      >
                        <Row>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label htmlFor="firstName" className="form-label">
                                {t("Fields.First Name")}*
                              </Label>
                              <Input
                                type="text"
                                className="form-control"
                                id="firstName"
                                placeholder={t("Fields.Enter your first name")}
                                value={profileForm.values.firstName || ""}
                                onChange={profileForm.handleChange}
                                onBlur={profileForm.handleBlur}
                                invalid={
                                  profileForm.touched.firstName &&
                                  profileForm.errors.firstName
                                    ? true
                                    : false
                                }
                              />
                              {profileForm.touched.firstName &&
                              profileForm.errors.firstName ? (
                                <FormFeedback type="invalid">
                                  {profileForm.errors.firstName}
                                </FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label htmlFor="lastName" className="form-label">
                                {t("Fields.Last Name")}*
                              </Label>
                              <Input
                                type="text"
                                className="form-control"
                                id="lastName"
                                placeholder={t("Fields.Enter your last name")}
                                value={profileForm.values.lastName || ""}
                                onChange={profileForm.handleChange}
                                onBlur={profileForm.handleBlur}
                                invalid={
                                  profileForm.touched.lastName &&
                                  profileForm.errors.lastName
                                    ? true
                                    : false
                                }
                              />
                              {profileForm.touched.lastName &&
                              profileForm.errors.lastName ? (
                                <FormFeedback type="invalid">
                                  {profileForm.errors.lastName}
                                </FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label htmlFor="mobile" className="form-label">
                                {t("Fields.Mobile")}
                              </Label>
                              <Input
                                type="text"
                                className="form-control"
                                id="mobile"
                                placeholder={t("Fields.Enter your mobile")}
                                value={profileForm.values.mobile || ""}
                                onChange={profileForm.handleChange}
                                onBlur={profileForm.handleBlur}
                                invalid={
                                  profileForm.touched.mobile &&
                                  profileForm.errors.mobile
                                    ? true
                                    : false
                                }
                              />
                              {profileForm.touched.mobile &&
                              profileForm.errors.mobile ? (
                                <FormFeedback type="invalid">
                                  {profileForm.errors.mobile}
                                </FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor="emailInput"
                                className="form-label"
                              >
                                {t("Email")}
                              </Label>
                              <Input
                                type="email"
                                className="form-control"
                                id="emailInput"
                                readOnly
                                disabled
                                defaultValue={user?.email}
                              />
                            </div>
                          </Col>
                          <Col lg={12}>
                            <div className="hstack gap-2 justify-content-end">
                              <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading}
                              >
                                {loading ? (
                                  <span className="d-flex align-items-center">
                                    <span className="flex-grow-1 me-2">
                                      {t("Common.Updating")}...
                                    </span>
                                    <Spinner
                                      size="sm"
                                      className="flex-shrink-0"
                                      role="status"
                                    >
                                      {" "}
                                      {t("Common.Updating")}...{" "}
                                    </Spinner>
                                  </span>
                                ) : (
                                  <>{t("Common.Update")}</>
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-soft-success"
                              >
                                {t("Common.Cancel")}
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </TabPane>
                    <TabPane tabId="2">
                      {chPassError && chPassError ? (
                        <Alert color="danger" style={{ marginTop: "13px" }}>
                          {t(`Messages.Error.${chPassError}`)}{" "}
                        </Alert>
                      ) : null}
                      {chPassSuccess ? (
                        <Alert color="success" style={{ marginTop: "13px" }}>
                          {t(`Messages.Success.${chPassSuccess}`)}
                        </Alert>
                      ) : null}
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          changePasswordForm.handleSubmit();
                          return false;
                        }}
                        action="#"
                      >
                        <Row className="g-2">
                          <Col lg={4}>
                            <div>
                              <Label
                                htmlFor="oldPassword"
                                className="form-label"
                              >
                                {t("Fields.Current Password")}*
                              </Label>
                              <Input
                                type="password"
                                className="form-control"
                                id="oldPassword"
                                placeholder={t("Enter current password")}
                                value={
                                  changePasswordForm.values.oldPassword || ""
                                }
                                onChange={changePasswordForm.handleChange}
                                onBlur={changePasswordForm.handleBlur}
                                invalid={
                                  changePasswordForm.touched.oldPassword &&
                                  changePasswordForm.errors.oldPassword
                                    ? true
                                    : false
                                }
                              />
                              {changePasswordForm.touched.oldPassword &&
                              changePasswordForm.errors.oldPassword ? (
                                <FormFeedback type="invalid">
                                  {changePasswordForm.errors.oldPassword}
                                </FormFeedback>
                              ) : null}
                            </div>
                          </Col>

                          <Col lg={4}>
                            <div>
                              <Label htmlFor="password" className="form-label">
                                {t("Fields.New Password")}*
                              </Label>
                              <Input
                                type="password"
                                className="form-control"
                                id="password"
                                placeholder={t("Fields.Enter New Password")}
                                value={changePasswordForm.values.password || ""}
                                onChange={changePasswordForm.handleChange}
                                onBlur={changePasswordForm.handleBlur}
                                invalid={
                                  changePasswordForm.touched.password &&
                                  changePasswordForm.errors.password
                                    ? true
                                    : false
                                }
                              />
                              {changePasswordForm.touched.password &&
                              changePasswordForm.errors.password ? (
                                <FormFeedback type="invalid">
                                  {changePasswordForm.errors.password}
                                </FormFeedback>
                              ) : null}
                            </div>
                          </Col>

                          <Col lg={4}>
                            <div>
                              <Label
                                htmlFor="confirmPassword"
                                className="form-label"
                              >
                                {t("Fields.Confirm Password")}*
                              </Label>
                              <Input
                                type="password"
                                className="form-control"
                                id="confirmPassword"
                                placeholder={t("Fields.Enter Confirm Password")}
                                value={
                                  changePasswordForm.values.confirmPassword ||
                                  ""
                                }
                                onChange={changePasswordForm.handleChange}
                                onBlur={changePasswordForm.handleBlur}
                                invalid={
                                  changePasswordForm.touched.confirmPassword &&
                                  changePasswordForm.errors.confirmPassword
                                    ? true
                                    : false
                                }
                              />
                              {changePasswordForm.touched.confirmPassword &&
                              changePasswordForm.errors.confirmPassword ? (
                                <FormFeedback type="invalid">
                                  {changePasswordForm.errors.confirmPassword}
                                </FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                          <Col lg={12}>
                            <div className="text-end">
                              <button type="submit" className="btn btn-success">
                                {chPassLoading ? (
                                  <span className="d-flex align-items-center">
                                    <span className="flex-grow-1 me-2">
                                      {t("Common.Updating")}...
                                    </span>
                                    <Spinner
                                      size="sm"
                                      className="flex-shrink-0"
                                      role="status"
                                    >
                                      {" "}
                                      {t("Common.Updating")}...{" "}
                                    </Spinner>
                                  </span>
                                ) : (
                                  <>{t("Common.Change Password")}</>
                                )}
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </TabPane>
                    <TabPane tabId="3">
                      {chPassError && chPassError ? (
                        <Alert color="danger" style={{ marginTop: "13px" }}>
                          {t(`Messages.Error.${chPassError}`)}{" "}
                        </Alert>
                      ) : null}
                      {chPassSuccess ? (
                        <Alert color="success" style={{ marginTop: "13px" }}>
                          {t(`Messages.Success.${chPassSuccess}`)}
                        </Alert>
                      ) : null}
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          otherSettingsForm.handleSubmit();
                          return false;
                        }}
                        action="#"
                      >
                        <Row className="g-2">
                          <Col lg={4}>
                            <div>
                              <div className="form-check mb-4 mt-2">
                                <Input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="isPaymentEnabled"
                                  checked={
                                    otherSettingsForm.values.isPaymentEnabled
                                  }
                                  value={
                                    otherSettingsForm.values.isPaymentEnabled ||
                                    false
                                  }
                                  onChange={otherSettingsForm.handleChange}
                                  onBlur={otherSettingsForm.handleBlur}
                                />
                                <Label
                                  className="form-check-label"
                                  htmlFor="isPaymentEnabled"
                                >
                                  {t("Common.Enable Payment in Apps")}
                                </Label>
                              </div>
                            </div>
                          </Col>
                          <Col lg={4}>
                            <div>
                              <div className="form-check mb-4 mt-2">
                                <Input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="isPhoneVerificationRequiredForSignup"
                                  checked={
                                    otherSettingsForm.values
                                      .isPhoneVerificationRequiredForSignup
                                  }
                                  value={
                                    otherSettingsForm.values
                                      .isPhoneVerificationRequiredForSignup ||
                                    false
                                  }
                                  onChange={otherSettingsForm.handleChange}
                                  onBlur={otherSettingsForm.handleBlur}
                                />
                                <Label
                                  className="form-check-label"
                                  htmlFor="isPhoneVerificationRequiredForSignup"
                                >
                                  {t(
                                    "Common.Enable Mobile Verification at Signup in Apps"
                                  )}
                                </Label>
                              </div>
                            </div>
                          </Col>
                          <Col lg={12}>
                            <div className="text-end">
                              <button type="submit" className="btn btn-success">
                                {updateSettingsLoading ? (
                                  <span className="d-flex align-items-center">
                                    <span className="flex-grow-1 me-2">
                                      {t("Common.Updating")}...
                                    </span>
                                    <Spinner
                                      size="sm"
                                      className="flex-shrink-0"
                                      role="status"
                                    >
                                      {" "}
                                      {t("Common.Updating")}...{" "}
                                    </Spinner>
                                  </span>
                                ) : (
                                  <>{t("Common.Update")}</>
                                )}
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SettingsPage;
